import { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { StudioSidebar } from "./components/StudioSidebar";
import { NewProductModal } from "./components/Sidebar";
import { Gallery } from "./pages/Gallery";
import { Performance } from "./pages/Performance";
import { AssetLibrary } from "./pages/AssetLibrary";
import { CreativeTree } from "./pages/CreativeTree";
import { ProductDashboard } from "./pages/ProductDashboard";
import { StudioChat, FormatsPage } from "./pages/DesignPreview";
import { ProductSettings } from "./pages/ProductSettings";
import { AppSettings } from "./pages/AppSettings";
import { Login } from "./pages/Login";
import { api, getToken, clearToken } from "./api";
import type { Product } from "@acs/shared";

function ErrorToast() {
  const [toasts, setToasts] = useState<{ id: number; msg: string }[]>([]);

  useEffect(() => {
    let counter = 0;
    function handler(e: Event) {
      const msg = (e as CustomEvent<string>).detail;
      const id = ++counter;
      setToasts((t) => [...t, { id, msg }]);
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
    }
    window.addEventListener("acs-api-error", handler);
    return () => window.removeEventListener("acs-api-error", handler);
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="bg-red-600 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg max-w-xs animate-fade-in">
          {t.msg}
        </div>
      ))}
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(() => getToken() ? null : false);

  useEffect(() => {
    if (authed !== null) return;
    api.verifyToken()
      .then(() => setAuthed(true))
      .catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (authed === null) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-zinc-400">Yükleniyor…</div>;
  if (!authed) {
    return <Login onLogin={() => setAuthed(true)} />;
  }
  return (
    <>
      <AuthedApp onLogout={() => { clearToken(); setAuthed(false); }} />
      <ErrorToast />
    </>
  );
}

function AuthedApp({ onLogout }: { onLogout: () => void }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  async function reload() {
    const res = await api.listProducts();
    setProducts(res.products);
  }

  useEffect(() => {
    reload().finally(() => setLoading(false));
    const onChanged = () => reload();
    window.addEventListener("products:changed", onChanged);
    return () => window.removeEventListener("products:changed", onChanged);
  }, []);

  return (
    <div className="flex h-screen">
      <main className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-zinc-500">Yükleniyor…</div>
        ) : (
          <Routes>
            <Route path="/" element={<Home products={products} onProductsChanged={reload} />} />
            <Route path="/products/:productId/*" element={<ProductLayout products={products} onProductsChanged={reload} onLogout={onLogout} />} />
            <Route path="/settings" element={<AppSettings />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function Home({ products, onProductsChanged }: { products: Product[]; onProductsChanged: () => Promise<void> | void }) {
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (products[0]) navigate(`/products/${products[0].id}/dashboard`, { replace: true });
  }, [products, navigate]);

  if (products.length > 0) return null;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">AI Creative Station</h1>
      <p className="text-zinc-600 mt-2">Henüz ürün yok.</p>
      <button
        onClick={() => setShowNew(true)}
        className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-medium"
        style={{ background: "#5E6AD2" }}
      >
        + Yeni Ürün
      </button>
      {showNew && (
        <NewProductModal
          onClose={() => setShowNew(false)}
          onSaved={async (newId) => {
            setShowNew(false);
            await onProductsChanged();
            navigate(`/products/${newId}/dashboard`);
          }}
        />
      )}
    </div>
  );
}

// ── ProductLayout — yeni UI'nın kalıcı kabuğu ───────────────────────────────
// Eski tab bar ve outer ürün-switcher sidebar'ı tamamen kaldırıldı.
// StudioSidebar hiçbir route değişiminde unmount olmaz ve artık ürün
// seçimi/oluşturma da kendi logo dropdown'ından yapılır.
function ProductLayout({
  products, onProductsChanged, onLogout,
}: {
  products: Product[];
  onProductsChanged: () => Promise<void> | void;
  onLogout: () => void;
}) {
  const { productId } = useParams();
  const [productName, setProductName] = useState<string | undefined>();
  if (!productId) return null;

  useEffect(() => {
    api.getProduct(productId).then(r => setProductName(r.product.name)).catch(() => {});
  }, [productId]);

  return (
    <div style={{ display: "flex", height: "100%", width: "100%", overflow: "hidden", background: "#fcfcfd" }}>
      <StudioSidebar
        productId={productId}
        productName={productName}
        products={products}
        onProductsChanged={onProductsChanged}
        onLogout={onLogout}
      />
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Routes>
          <Route path="dashboard" element={<ProductDashboard productId={productId} />} />
          <Route path="studio" element={<StudioChat productId={productId} productName={productName} />} />
          <Route path="formats" element={<FormatsPage productId={productId} />} />
          <Route path="tree" element={<ScrollWrap><CreativeTree productId={productId} /></ScrollWrap>} />
          <Route path="gallery" element={<ScrollWrap><Gallery productId={productId} /></ScrollWrap>} />
          <Route path="performance" element={<ScrollWrap><Performance productId={productId} /></ScrollWrap>} />
          <Route path="assets" element={<AssetLibrary productId={productId} />} />
          <Route path="settings" element={<ScrollWrap><ProductSettings productId={productId} /></ScrollWrap>} />
          <Route path="builder" element={<ScrollWrap><CreativeBuilderLazy productId={productId} /></ScrollWrap>} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// Sade sayfalar (Tailwind tabanlı, kendi scroll yönetimi yok) için sarmalayıcı.
function ScrollWrap({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>;
}

// CreativeBuilder ağır bir bileşen (Canvas/@xyflow içeriyor) — sadece
// "+ Yeni Tip" butonuna basılınca lazy-load edilsin.
const CreativeBuilderInner = lazy(() => import("./pages/CreativeBuilder").then(m => ({ default: m.CreativeBuilder })));
function CreativeBuilderLazy({ productId }: { productId: string }) {
  return (
    <Suspense fallback={<div className="p-8 text-sm text-zinc-400">Yükleniyor…</div>}>
      <CreativeBuilderInner productId={productId} />
    </Suspense>
  );
}
